import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

import "./FormStyles.css";
import useAxios from "./useAxios";
import useSessionStorage from "./useSessionStorage";

import moment from "moment";

const UserTournament = () => {
  const { id } = useParams();
  const [axios] = useAxios(true);
  const navigate = useNavigate();

  const [competition, setCompetition] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isPlayerInTournament, setIsPlayerInTournament] = useState(false);
  const [playerEntry, setPlayerEntry] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Fetch competition details with player data
    axios.get("competitionknockout/" + id, (knockoutData) => {
      // Convert knockout data to competition format
      const competitionData = {
        id: id,
        name: knockoutData.competition?.name || 'Tournament',
        dateTime: knockoutData.competition?.dateTime,
        type: knockoutData.competition?.type,
        raceTo: knockoutData.raceTo,
        matchTimeLimit: knockoutData.matchTimeLimit,
        handicapped: knockoutData.handicapped,
        players: knockoutData.players || []
      };
      setCompetition(competitionData);
    });
    
    // Fetch user profile to check if user has a linked player
    axios.get("user/profile", (profile) => {
      setUserProfile(profile);
    });
  }, [id]);

  useEffect(() => {
    if (userProfile?.playerId && competition) {
      checkPlayerInTournament(userProfile.playerId);
    }
  }, [userProfile, competition]);

  const checkPlayerInTournament = (playerId) => {
    if (!competition?.players) return;
    
    const entry = competition.players.find(p => p.id === playerId);
    if (entry) {
      setIsPlayerInTournament(true);
      setPlayerEntry(entry);
    } else {
      setIsPlayerInTournament(false);
      setPlayerEntry(null);
    }
  };

  const handleJoinTournament = () => {
    if (!userProfile?.playerId) return;
    
    setIsLoading(true);
    
    axios.post(`usertournament/${id}/join`, {}, (response) => {
      // Refresh competition data
      axios.get("competitionknockout/" + id, (knockoutData) => {
        const competitionData = {
          id: id,
          name: knockoutData.competition?.name || 'Tournament',
          dateTime: knockoutData.competition?.dateTime,
          type: knockoutData.competition?.type,
          raceTo: knockoutData.raceTo,
          matchTimeLimit: knockoutData.matchTimeLimit,
          handicapped: knockoutData.handicapped,
          players: knockoutData.players || []
        };
        setCompetition(competitionData);
        setIsPlayerInTournament(true);
        setPlayerEntry({
          id: userProfile.playerId,
          name: userProfile.player.name,
          handicap: userProfile.player.handicap,
          paid: false
        });
        setIsLoading(false);
      });
    }, (error) => {
      console.error("Error joining tournament:", error);
      setIsLoading(false);
    });
  };

  const handleLeaveTournament = () => {
    if (!userProfile?.playerId) return;
    
    setIsLoading(true);
    
    axios.post(`usertournament/${id}/leave`, {}, (response) => {
      // Refresh competition data
      axios.get("competitionknockout/" + id, (knockoutData) => {
        const competitionData = {
          id: id,
          name: knockoutData.competition?.name || 'Tournament',
          dateTime: knockoutData.competition?.dateTime,
          type: knockoutData.competition?.type,
          raceTo: knockoutData.raceTo,
          matchTimeLimit: knockoutData.matchTimeLimit,
          handicapped: knockoutData.handicapped,
          players: knockoutData.players || []
        };
        setCompetition(competitionData);
        setIsPlayerInTournament(false);
        setPlayerEntry(null);
        setIsLoading(false);
      });
    }, (error) => {
      console.error("Error leaving tournament:", error);
      setIsLoading(false);
    });
  };

  if (competition === null || userProfile === null) {
    return <h1>Loading...</h1>;
  }

  const canJoinLeave = userProfile.playerId !== null;

  return (
    <div className="edit-competition-container">
      <h2>{competition.name}</h2>
      
      <div className="competition-form-fields" style={{ opacity: 1 }}>
        <div className="form-group">
          <label>Date/Time:</label>
          <span>{moment(competition.dateTime).format("DD/MM/yyyy HH:mm")}</span>
        </div>
        
        <div className="form-group">
          <label>Type:</label>
          <span>{competition.type?.name}</span>
        </div>

        {competition.type?.id === "knockout" && (
          <>
            <div className="form-group">
              <label>Race To:</label>
              <span>{competition.raceTo}</span>
            </div>
            
            <div className="form-group">
              <label>Match Time Limit:</label>
              <span>{competition.matchTimeLimit} minutes</span>
            </div>
            
            <div className="form-group">
              <label>Handicapped:</label>
              <span>{competition.handicapped ? "Yes" : "No"}</span>
            </div>
          </>
        )}

        <div className="form-group">
          <label>Entries ({(competition.players || []).length}):</label>
          <div className="selected-players-list">
            {(competition.players || []).map((player) => (
              <div key={player.id} className="selected-player" style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ flex: 1 }}>
                  {player.name} ({player.handicap})
                </div>
                <div style={{ marginLeft: '12px', fontSize: '0.9em', color: player.paid ? '#28a745' : '#dc3545' }}>
                  {player.paid ? "Paid" : "Unpaid"}
                </div>
              </div>
            ))}
            {(competition.players || []).length === 0 && (
              <div style={{ color: '#666', fontStyle: 'italic' }}>No players registered yet</div>
            )}
          </div>
        </div>

        {canJoinLeave && (
          <div className="form-group">
            <label>Your Participation:</label>
            <div style={{ marginTop: '8px' }}>
              {!isPlayerInTournament ? (
                <button
                  type="button"
                  onClick={handleJoinTournament}
                  disabled={isLoading}
                  className="submit-button"
                  style={{ marginRight: '12px' }}
                >
                  {isLoading ? "Joining..." : "Join Tournament"}
                </button>
              ) : (
                <div>
                  <div style={{ marginBottom: '8px', color: '#28a745' }}>
                    ✓ You are registered for this tournament
                    {playerEntry && (
                      <span style={{ marginLeft: '8px', fontSize: '0.9em', color: playerEntry.paid ? '#28a745' : '#dc3545' }}>
                        ({playerEntry.paid ? "Paid" : "Unpaid"})
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleLeaveTournament}
                    disabled={isLoading}
                    className="cancel-button"
                  >
                    {isLoading ? "Leaving..." : "Leave Tournament"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {!canJoinLeave && (
          <div className="form-group">
            <div style={{ padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px', color: '#6c757d' }}>
              You need to have a player profile linked to your account to join tournaments. Please contact an administrator.
            </div>
          </div>
        )}

        <div className="form-buttons">
          <button type="button" onClick={() => navigate("/tournaments")} className="cancel-button">
            Back to Tournaments
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserTournament;